<template>
  <themed-modal
    name="manage-users"
    title="Manage Users"
    size="xl"
    custom-class="manage-users-modal"
    @opened="handleModalOpened"
    @closed="handleModalClosed"
  >
    <div class="manage-users">
      <!-- Toolbar -->
      <div class="manage-users__toolbar">
        <div class="manage-users__selectors">
          <div v-if="$gate.isAdmin()" class="manage-users__selector">
            <label>Client</label>
            <themed-select
              v-model="selectedClientId"
              :options="clientOptions"
              placeholder="Select Client"
            />
          </div>

          <div v-if="users.all.length" class="manage-users__selector">
            <label>User</label>
            <themed-select
              v-model="selectedUserId"
              :options="userOptions"
              placeholder="Select User"
            />
          </div>
        </div>

        <themed-button
          v-if="$gate.isAdmin() || $gate.isClientAdmin()"
          @click="createUser"
          variant="default"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>Create User</span>
        </themed-button>
      </div>

      <!-- Content with Sidebar -->
      <div class="manage-users__content">
        <!-- Sidebar Navigation -->
        <div class="manage-users__sidebar">
          <button
            @click="section = 'user-info'"
            :class="{ 'manage-users__nav-item--active': section === 'user-info' }"
            class="manage-users__nav-item"
          >
            User Info
          </button>

          <button
            v-if="selectedUserId && selectedUserId !== 'new' && isBrokerRole"
            @click="section = 'client-permissions'"
            :class="{ 'manage-users__nav-item--active': section === 'client-permissions' }"
            class="manage-users__nav-item"
          >
            Client Permissions
          </button>

          <button
            v-if="selectedUserId && selectedUserId !== 'new' && (!isBrokerRole || brokerHasClientPermissions)"
            @click="section = 'project-permissions'"
            :class="{ 'manage-users__nav-item--active': section === 'project-permissions' }"
            class="manage-users__nav-item"
          >
            Project Permissions
          </button>
          
          <!-- Disabled Project Permissions for Brokers without Client Access -->
          <div
            v-if="selectedUserId && selectedUserId !== 'new' && isBrokerRole && !brokerHasClientPermissions"
            class="manage-users__nav-item manage-users__nav-item--disabled"
            :title="'Save client permissions first to access project permissions'"
          >
            <span>Project Permissions</span>
            <theme-icon icon="lock" type="icons" classes="w-4 h-4 ml-2 opacity-50" />
          </div>

          <button
            v-if="selectedUserId && selectedUserId !== 'new' && (!isBrokerRole || brokerHasClientPermissions)"
            @click="section = 'site-permissions'"
            :class="{ 'manage-users__nav-item--active': section === 'site-permissions' }"
            class="manage-users__nav-item"
          >
            Site Permissions
          </button>
          
          <!-- Disabled Site Permissions for Brokers without Client Access -->
          <div
            v-if="selectedUserId && selectedUserId !== 'new' && isBrokerRole && !brokerHasClientPermissions"
            class="manage-users__nav-item manage-users__nav-item--disabled"
            :title="'Save client permissions first to access site permissions'"
          >
            <span>Site Permissions</span>
            <theme-icon icon="lock" type="icons" classes="w-4 h-4 ml-2 opacity-50" />
          </div>

          <button
            v-if="selectedUserId && selectedUserId !== 'new'"
            @click="section = 'module-permissions'"
            :class="{ 'manage-users__nav-item--active': section === 'module-permissions' }"
            class="manage-users__nav-item"
          >
            Module Permissions
          </button>
        </div>

        <!-- Main Content Area -->
        <div class="manage-users__main">
          <div v-show="section === 'user-info'" class="manage-users__section">
            <user-info />
          </div>

          <div v-show="section === 'client-permissions'" class="manage-users__section">
            <client-permissions :user-id="selectedUserId" />
          </div>

          <div v-show="section === 'project-permissions'" class="manage-users__section">
            <project-permissions :user-id="selectedUserId" />
          </div>

          <div v-show="section === 'site-permissions'" class="manage-users__section">
            <site-permissions :user-id="selectedUserId" />
          </div>

          <div v-show="section === 'module-permissions'" class="manage-users__section">
            <module-permissions
              :user-id="selectedUserId"
              permission-group="module"
            />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="modal-actions">
        <themed-button variant="secondary" @click="hideModal">
          Close
        </themed-button>
        <themed-button
          v-if="aUserIsSelected || selectedUserId === 'new'"
          variant="default"
          :disabled="loading"
          @click="save"
        >
          {{ loading ? 'Saving...' : 'Save' }}
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';
import UserInfo from './UserInfo.vue';
import ProjectPermissions from './permissions/ProjectPermissions.vue';
import SitePermissions from './permissions/SitePermissions.vue';
import ModulePermissions from './permissions/ModulePermissions.vue';
import ClientPermissions from './permissions/ClientPermissions.vue';

export default {
  name: 'ManageUsers',

  components: {
    ThemedModal,
    ThemedButton,
    ThemedSelect,
    ThemeIcon,
    UserInfo,
    ProjectPermissions,
    SitePermissions,
    ModulePermissions,
    ClientPermissions
  },

  props: {
    clientId: {
      type: Number,
      required: false
    },

    userId: {
      type: [Number, String],
      required: false,
      default: null
    },

    selectedSection: {
      type: String,
      default: 'user-info'
    }
  },

  data() {
    return {
      section: this.selectedSection,
      selectedClientId: this.clientId || '',
      selectedUserId: this.userId || '',
      loading: false
    };
  },

  computed: {
    ...mapState(['clients']),
    ...mapState(['users']),

    clientOptions() {
      if (!this.clients.all) return [];
      return this.clients.all.map(client => ({
        value: client.id,
        label: client.name
      }));
    },

    userOptions() {
      if (!this.users.all) return [];
      return this.users.all.map(user => ({
        value: user.id,
        label: user.name
      }));
    },

    client() {
      return _.find(this.clients.all, ['id', this.selectedClientId]);
    },

    aUserIsSelected() {
      return this.selectedUserId !== 'new' && this.selectedUserId !== '';
    },

    isBrokerRole() {
      const selectedUser = this.users.selectedUser;
      if (!selectedUser || !selectedUser.role) return false;
      return selectedUser.role.name === 'broker' || selectedUser.role_id === 4;
    },

    brokerHasClientPermissions() {
      // For non-broker users, always return true (show all tabs)
      if (!this.isBrokerRole) return true;
      
      // For broker users, check if they have any client permissions
      // This will be updated when ClientPermissions component saves or loads
      const selectedUser = this.users.selectedUser;
      if (!selectedUser) return false;
      
      // Check if the user has the hasClientPermissions flag set
      return selectedUser.hasClientPermissions || false;
    }
  },

  watch: {
    userId: {
      immediate: true,
      handler(newUserId) {
        if (newUserId && newUserId !== '') {
          this.selectedUserId = newUserId;
        }
      }
    },

    selectedClientId() {
      this.selectedUserId = '';
      this.users.all = this.client?.users || [];
    },

    selectedUserId(newUserId) {
      const selectedUser = _.find(this.users.all, ['id', newUserId]) || '';
      this.users.selectedUser = selectedUser;
      
      // If broker user is selected, initialize flag to false and then check permissions
      if (selectedUser && newUserId !== 'new') {
        const isBroker = selectedUser.role && (selectedUser.role.name === 'broker' || selectedUser.role_id === 4);
        
        if (isBroker) {
          // Initialize to false for brokers
          this.$set(this.users.selectedUser, 'hasClientPermissions', false);
          // Then check actual permissions
          this.checkBrokerClientPermissions(newUserId);
        } else {
          // Non-brokers always have access
          this.$set(this.users.selectedUser, 'hasClientPermissions', true);
        }
      }
    }
  },

  created() {
    // Pull clients if admin
    if (this.$gate.isAdmin() && !this.clients.all) {
      axios.get('/api/clients').then((response) => {
        this.clients.all = response.data;
      }).catch((error) => {
        console.log(error);
      });
    }

    // If not admin or client admin, set to current user
    if (!this.$gate.isAdmin() && !this.$gate.isClientAdmin()) {
      this.selectedUserId = window.user.id;
    }

    // Pull requested user
    if (this.selectedUserId) {
      this.users.selectedUser = _.find(this.users.all, ['id', this.selectedUserId]);
    }
  },

  mounted() {
    this.$eventHub.$on('user-saved', () => {
      this.loading = false;
      
      // If a new user was saved, remove the 'new' placeholder and reload users
      if (this.selectedUserId === 'new' && this.users.selectedUser.id !== 'new') {
        // Remove the 'new' placeholder
        this.users.all = this.users.all.filter(user => user.id !== 'new');
        
        // Add the newly created user to the array if not already there
        const existingUser = _.find(this.users.all, ['id', this.users.selectedUser.id]);
        if (!existingUser) {
          this.users.all.push(this.users.selectedUser);
        }
        
        // Update the selected user ID to the new user's actual ID
        this.selectedUserId = this.users.selectedUser.id;
      }
    });

    this.$eventHub.$on('user-save-error', () => {
      this.loading = false;
    });

    // Listen for client permission changes to update broker access
    this.$eventHub.$on('client-permissions-updated', (hasPermissions) => {
      if (this.users.selectedUser) {
        this.$set(this.users.selectedUser, 'hasClientPermissions', hasPermissions);
      }
    });
  },

  beforeDestroy() {
    this.$eventHub.$off('user-saved');
    this.$eventHub.$off('user-save-error');
    this.$eventHub.$off('client-permissions-updated');
  },

  methods: {
    hideModal() {
      this.$modal.hide('manage-users');
    },

    handleModalOpened() {
      // Emit event to reload data in child components
      this.$eventHub.$emit('modal-opened-manage-users');
    },

    handleModalClosed() {
      // Remove any unsaved 'new' user from the array
      this.users.all = this.users.all.filter(user => user.id !== 'new');
      
      // Reset state when modal closes
      this.section = 'user-info';
      this.selectedUserId = this.userId || null;
      this.loading = false;
      
      // Emit closed event to parent
      this.$emit('closed');
    },

    async checkBrokerClientPermissions(userId) {
      // Only check for broker users
      const selectedUser = this.users.selectedUser;
      if (!selectedUser || !selectedUser.role) return;
      
      const isBroker = selectedUser.role.name === 'broker' || selectedUser.role_id === 4;
      if (!isBroker) {
        // Non-broker users always have access to all tabs
        this.$set(this.users.selectedUser, 'hasClientPermissions', true);
        return;
      }
      
      try {
        // Fetch client permissions for this broker
        const response = await axios.get(`/api/users/${userId}/permissions/client`);
        const responseData = response.data?.data || {};
        const items = responseData.items || [];
        
        // Check if broker has any client permissions (either global or entity-level)
        let hasPermissions = false;
        
        if (Array.isArray(items) && items.length > 0) {
          // Check for global permissions (item with id === null)
          const globalItem = items.find(item => item.id === null);
          if (globalItem && globalItem.permissions) {
            hasPermissions = Object.values(globalItem.permissions).some(val => val === true);
          }
          
          // Check for entity-level permissions (items with id !== null)
          if (!hasPermissions) {
            const entityItems = items.filter(item => item.id !== null);
            hasPermissions = entityItems.some(item => {
              return item.permissions && Object.values(item.permissions).some(val => val === true);
            });
          }
        }
        
        console.log('Broker client permissions check:', { userId, hasPermissions, items });
        this.$set(this.users.selectedUser, 'hasClientPermissions', hasPermissions);
      } catch (error) {
        console.error('Error checking broker client permissions:', error);
        // Default to false if there's an error
        this.$set(this.users.selectedUser, 'hasClientPermissions', false);
      }
    },

    createUser() {
      // Check if a 'new' user already exists in the array
      const existingNewUser = _.find(this.users.all, ['id', 'new']);
      
      if (!existingNewUser) {
        // Only add if it doesn't exist
        this.users.all.push({
          id: 'new',
          client_id: this.selectedClientId,
          role: {},
          country_id: 16
        });
      }

      this.selectedUserId = 'new';
      this.section = 'user-info';
    },

    async save() {
      console.log('Save clicked, section:', this.section, 'userId:', this.selectedUserId);
      this.loading = true;
      
      try {
        let savedSections = [];
        let sectionsWithChanges = [];
        let sectionsWithoutChanges = [];

        // Save User Info if on that tab
        if (this.section === 'user-info') {
          console.log('Saving user info...');
          this.$eventHub.$emit('save-user-user-info');
          // Wait for user-info to complete
          await new Promise((resolve) => {
            const handler = () => {
              this.$eventHub.$off('user-saved', handler);
              this.$eventHub.$off('user-save-error', handler);
              savedSections.push('User Info');
              sectionsWithChanges.push('User Info');
              resolve();
            };
            this.$eventHub.$on('user-saved', handler);
            this.$eventHub.$on('user-save-error', handler);
            
            // Timeout after 10 seconds
            setTimeout(() => {
              this.$eventHub.$off('user-saved', handler);
              this.$eventHub.$off('user-save-error', handler);
              resolve();
            }, 10000);
          });
        } 
        // Save all permission tabs if user is selected and not new
        else if (this.selectedUserId && this.selectedUserId !== 'new') {
          console.log('Saving all permissions...');
          
          // Determine how many permission tabs to wait for
          const totalPermissionTabs = this.isBrokerRole ? 4 : 3; // client, project, site, module for brokers; project, site, module for others
          
          // Set up listeners BEFORE emitting the event
          await new Promise((resolve) => {
            let completedCount = 0;
            let timeoutId = null;
            
            const savedHandler = (data) => {
              console.log('Permission saved:', data);
              completedCount++;
              if (data && data.section) {
                if (data.hasChanges) {
                  sectionsWithChanges.push(data.section);
                } else {
                  sectionsWithoutChanges.push(data.section);
                }
                savedSections.push(data.section);
              }
              
              if (completedCount >= totalPermissionTabs) {
                console.log('All permissions completed, count:', completedCount);
                console.log('Sections with changes:', sectionsWithChanges);
                console.log('Sections without changes:', sectionsWithoutChanges);
                
                // Clear timeout
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
                
                this.$eventHub.$off('permission-saved', savedHandler);
                this.$eventHub.$off('permission-save-error', errorHandler);
                resolve();
              }
            };
            
            const errorHandler = (data) => {
              console.log('Permission save error:', data);
              completedCount++;
              
              if (completedCount >= totalPermissionTabs) {
                console.log('All permissions completed (with errors), count:', completedCount);
                
                // Clear timeout
                if (timeoutId) {
                  clearTimeout(timeoutId);
                }
                
                this.$eventHub.$off('permission-saved', savedHandler);
                this.$eventHub.$off('permission-save-error', errorHandler);
                resolve();
              }
            };
            
            // Register handlers FIRST
            this.$eventHub.$on('permission-saved', savedHandler);
            this.$eventHub.$on('permission-save-error', errorHandler);
            
            // THEN emit the event
            this.$nextTick(() => {
              this.$eventHub.$emit('save-all-permissions');
            });
            
            // Timeout after 10 seconds
            timeoutId = setTimeout(() => {
              console.log('Permission save timeout, completed:', completedCount);
              this.$eventHub.$off('permission-saved', savedHandler);
              this.$eventHub.$off('permission-save-error', errorHandler);
              resolve();
            }, 10000);
          });
        }

        console.log('After promise - Saved sections:', savedSections);
        console.log('After promise - Sections with changes:', sectionsWithChanges);
        console.log('After promise - Sections without changes:', sectionsWithoutChanges);
        
        // Show notification after promise resolves
        if (sectionsWithChanges.length > 0) {
          console.log('Showing success notification for:', sectionsWithChanges);
          this.$notify({
            type: 'success',
            title: 'Success',
            text: `Saved changes in: ${sectionsWithChanges.join(', ')}`
          });
        } else if (savedSections.length > 0) {
          console.log('Showing info notification - no changes');
          console.log('$notify available?', typeof this.$notify);
          this.$notify({
            type: 'info',
            title: 'No Changes',
            text: 'No permission changes to save'
          });
        }
      } catch (error) {
        console.error('Error saving:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'An error occurred while saving'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
